import { FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CabReportStatus, type SalesLead, useFetchCabReport } from '@/models/salesLead'

export function isCabReportReady(cabReport: SalesLead['cabReport']): boolean {
  return cabReport?.status === CabReportStatus.READY && !!cabReport.fileUrl
}

interface CabReportButtonProps {
  lead: SalesLead
  className?: string
}

export function CabReportButton({ lead, className }: CabReportButtonProps) {
  const fetchCabReportMutation = useFetchCabReport()
  const cabReport = lead.cabReport
  const ready = isCabReportReady(cabReport)
  const pending = cabReport?.status === CabReportStatus.PENDING || fetchCabReportMutation.isPending

  function handleClick() {
    if (ready && cabReport?.fileUrl) {
      window.open(cabReport.fileUrl, '_blank', 'noopener,noreferrer')
      return
    }
    fetchCabReportMutation.mutate(lead.id)
  }

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={!lead.dotNumber}
      loading={pending ? 'Fetching report…' : undefined}
      title={
        !lead.dotNumber
          ? 'Lead must have a DOT number to fetch a CAB report'
          : cabReport?.status === CabReportStatus.FAILED
            ? 'Last fetch failed — click to retry'
            : undefined
      }
      className={cn(
        'w-full justify-start',
        ready && 'border-gray-300 bg-gray-100 text-gray-900 hover:bg-gray-200',
        className
      )}
    >
      <FileText className="mr-2 size-4" />
      {ready ? 'View CAB Report' : 'Get CAB Report'}
    </Button>
  )
}
